/**
 * Simple side-view silhouettes, one per SLTC vehicle category.
 *
 * Drawn inline as SVG rather than shipped as images: they stay crisp at any
 * size, inherit `currentColor` so they dim with a disabled card, and add
 * nothing to the network payload. Everything sits in a 64 x 28 viewBox with the
 * road line at y = 24, so the whole set reads as one family.
 *
 * These are recognition aids, not engineering drawings — for the articulated
 * types the axle notation printed on the card is the authoritative difference.
 */

interface VehicleIconProps {
  vehicleTypeId: string;
  className?: string;
}

const VIEW_BOX = '0 0 64 28';

/** Road wheel. */
function W({ cx, r = 3.4 }: { cx: number; r?: number }) {
  return <circle cx={cx} cy={24 - r} r={r} />;
}

/** Renders the body shapes then the wheels, so wheels always sit on top. */
function Icon({ body, wheels }: { body: React.ReactNode; wheels: number[] }) {
  return (
    <>
      {body}
      {wheels.map((cx) => (
        <W key={cx} cx={cx} />
      ))}
    </>
  );
}

const ICONS: Record<string, React.ReactNode> = {
  // Motorcycle: solid wheels with a tank/seat body between them, drawn filled
  // like the rest of the set so it does not read as a bare line at small sizes.
  mcl: (
    <>
      <path d="M17 20 L23 20 L26 14 L34 14 L36 11 L41 11 L41 13 L37 13 L36 16 L44 19 L44 21 L36 21 L30 17 L26 21 Z" />
      <path d="M20 12 L28 12 L28 14 L20 14 Z" />
      <circle cx="17" cy="20.6" r="3.6" />
      <circle cx="46" cy="20.6" r="3.6" />
      <circle cx="17" cy="20.6" r="1.3" className="fill-white" />
      <circle cx="46" cy="20.6" r="1.3" className="fill-white" />
    </>
  ),

  // Three wheeler: rounded cabin, one front wheel, two at the back.
  tw: (
    <Icon body={<path d="M18 20.6 L18 13 Q22 7 32 7 Q44 7 47 14 L47 20.6 Z" />} wheels={[19, 45]} />
  ),

  // Car: low bonnet, sloped cabin.
  car: (
    <Icon
      body={<path d="M9 20.6 L10 15 L20 15 L25 9.5 L41 9.5 L46 15 L55 15 L55 20.6 Z" />}
      wheels={[19, 46]}
    />
  ),

  // Van: taller box with a short bonnet.
  van: <Icon body={<path d="M8 20.6 L8 11 L14 7 L52 7 L54 20.6 Z" />} wheels={[17, 46]} />,

  // Medium bus: box body, shorter than the large bus.
  mbus: (
    <Icon
      body={
        <>
          <path d="M9 20.6 L9 8 L51 8 L53 20.6 Z" />
          <rect x="13" y="11" width="34" height="4" className="fill-white/70" />
        </>
      }
      wheels={[18, 45]}
    />
  ),

  // Large bus: full-length box, longer window band.
  lbus: (
    <Icon
      body={
        <>
          <path d="M4 20.6 L4 6.5 L58 6.5 L60 20.6 Z" />
          <rect x="8" y="9.5" width="48" height="5" className="fill-white/70" />
        </>
      }
      wheels={[13, 49]}
    />
  ),

  // Pick up: cab plus an open bed (1-1).
  lgv: (
    <Icon
      body={
        <>
          <path d="M7 20.6 L7 12 L13 12 L16 7 L30 7 L32 12 L32 20.6 Z" />
          <path d="M32 20.6 L32 13 L57 13 L57 20.6 Z" />
        </>
      }
      wheels={[16, 47]}
    />
  ),

  // Medium truck 1: short cab, box body (1-2).
  mg1: (
    <Icon
      body={
        <>
          <path d="M5 20.6 L5 10 L15 10 L17 20.6 Z" />
          <path d="M18 20.6 L18 7.5 L56 7.5 L56 20.6 Z" />
        </>
      }
      wheels={[11, 47]}
    />
  ),

  // Medium truck 2: same shape, heavier (taller) body.
  mg2: (
    <Icon
      body={
        <>
          <path d="M4 20.6 L4 9 L15 9 L17 20.6 Z" />
          <path d="M18 20.6 L18 5.5 L58 5.5 L58 20.6 Z" />
        </>
      }
      wheels={[10, 47]}
    />
  ),

  // Heavy truck: rigid, twin rear axle (1-22).
  hg3: (
    <Icon
      body={
        <>
          <path d="M3 20.6 L3 9 L14 9 L16 20.6 Z" />
          <path d="M17 20.6 L17 5.5 L60 5.5 L60 20.6 Z" />
        </>
      }
      wheels={[9, 45, 54]}
    />
  ),

  // Articulated, 3 axles: tractor unit plus semi-trailer (1-2-2).
  ag3: (
    <Icon
      body={
        <>
          <path d="M3 20.6 L3 9 L13 9 L15 18 L15 20.6 Z" />
          <path d="M15 18 L60 18 L60 20.6 L15 20.6 Z" />
          <path d="M18 18 L18 6 L60 6 L60 18 Z" />
        </>
      }
      wheels={[8, 30, 52]}
    />
  ),

  // 4 axles.
  ag4: (
    <Icon
      body={
        <>
          <path d="M3 20.6 L3 9 L13 9 L15 18 L15 20.6 Z" />
          <path d="M15 18 L61 18 L61 20.6 L15 20.6 Z" />
          <path d="M18 18 L18 6 L61 6 L61 18 Z" />
        </>
      }
      wheels={[8, 26, 48, 57]}
    />
  ),

  // 5 axles.
  ag5: (
    <Icon
      body={
        <>
          <path d="M2 20.6 L2 9 L12 9 L14 18 L14 20.6 Z" />
          <path d="M14 18 L62 18 L62 20.6 L14 20.6 Z" />
          <path d="M17 18 L17 6 L62 6 L62 18 Z" />
        </>
      }
      wheels={[7, 22, 31, 49, 58]}
    />
  ),

  // 6 axles.
  ag6: (
    <Icon
      body={
        <>
          <path d="M2 20.6 L2 9 L12 9 L14 18 L14 20.6 Z" />
          <path d="M14 18 L62 18 L62 20.6 L14 20.6 Z" />
          <path d="M17 18 L17 6 L62 6 L62 18 Z" />
        </>
      }
      wheels={[7, 21, 30, 44, 52, 60]}
    />
  ),

  // Tractor: small front wheel, large driven rear wheel, flat trailer.
  fvh: (
    <>
      <path d="M8 21 L8 13 L14 13 L16 8 L24 8 L24 21 Z" />
      <path d="M26 20 L58 20 L58 16 L26 16 Z" />
      <circle cx="11" cy="20" r="2.6" />
      <circle cx="21" cy="18.4" r="5.6" />
      <circle cx="50" cy="21.4" r="2.6" />
    </>
  ),
};

/**
 * Falls back to a neutral box on wheels for any id without a drawing, so a new
 * category added to the config still renders something sensible.
 */
const FALLBACK = <Icon body={<path d="M8 20.6 L8 9 L56 9 L56 20.6 Z" />} wheels={[17, 47]} />;

export function VehicleIcon({ vehicleTypeId, className }: VehicleIconProps) {
  return (
    <svg
      viewBox={VIEW_BOX}
      className={className}
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="0"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICONS[vehicleTypeId] ?? FALLBACK}
    </svg>
  );
}
